import { useFormik } from "formik";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { Input, Switch, Textarea } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import { formsSubmitAtom } from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import PhoneInput from "../../../components/forms/PhoneInputWrapper";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useProviderTypes from "../../../hooks/api-calls/useProviderTypes";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { ProviderService } from "../../../services/providers/providerService";
import { Provider } from "../../../type/entities/provider-type";
import { providerStatuses, retributionTypes } from "../../../utils/SelectorMapping";

interface ProviderFormProps {
    setOpenModal: any;
    refetch: () => void;
    provider?: Provider | null;
    formRef?: any;
}

const schema = yup.object({
    cif: yup.string().nullable(),
    companyName: yup.string().required('La razón social es obligatoria'),
    code: yup.string().required('El código es obligatorio'),
    commercialName: yup.string().nullable(),
    activity: yup.string().nullable(),
    website: yup.string().nullable(),
    phone: yup.string().nullable(),
    phone2: yup.string().nullable(),
    email: yup.string().nullable().email('El email es inválido'),
    retributionType: yup.string().required('El tipo de retribución es obligatorio'),
    providerTypeId: yup.string().required('El tipo de proveedor es obligatorio'),
    autoInvoice: yup.boolean().nullable(),
    taxDomicile: yup.string().nullable(),
    comments: yup.string().nullable(),
    statusId: yup.string().required('El estado es obligatorio'),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))



const ProviderForm: FC<ProviderFormProps> = ({ setOpenModal, refetch, provider, formRef }) => {

    // STATES

    const service = new ProviderService();
    const isEdit = provider !== null;
    const [isSearched, setIsSearched] = useState(false);

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: provider?.id ?? '',
            cif: provider?.cif ?? '',
            code: provider?.code ?? '',
            commercialName: provider?.commercialName ?? '',
            activity: provider?.activity ?? '',
            website: provider?.website ?? '',
            email: provider?.email ?? '',
            telephone: provider?.telephone ?? '',
            telephone2: provider?.telephone2 ?? '',
            companyName: provider?.companyName ?? '',
            retributionType: provider?.retributionType ?? '',
            providerTypeId: provider?.providerType?.id ?? '',
            comments: provider?.comments ?? '',
            company: provider?.company?.id ?? '',
            autoInvoice: provider?.autoInvoice ?? true,
            taxDomicile: provider?.taxDomicile ?? '',
            statusId: provider?.status?.id ?? 'activo',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    const { getProviderTypesList } = useProviderTypes(formik.values.company || null);

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO TIPO DE PROVEEDOR
     * @EN CREATE A NEW PROVIDER TYPE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createProvider(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Proveedor creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN TIPO DE PROVEEDOR
     * @EN EDIT A PROVIDER TYPE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editProvider(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Proveedor editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        const fetchLastCode = async () => {
            if (!isEdit && !formik.values.code) {
                try {
                    const response = await (await service.getLastCode()).getResponseData();
                    if (response.success) {
                        const lastCode = response.data.lastCode;
                        let nextCode = 'P1';
                        if (lastCode) {
                            const lastNumber = parseInt(lastCode.replace('P', ''));
                            if (!isNaN(lastNumber)) {
                                nextCode = 'P' + (lastNumber + 1);
                            }
                        }
                        formik.setFieldValue('code', nextCode);
                    }
                } catch (error) {
                    console.error("Error fetching last code:", error);
                }
            }
        };

        fetchLastCode();
    }, [isEdit]);

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        const companies = getCompaniesList();
        if (!isSearched && !formik.values.company && companies.length > 0) {
            const defaultCompany = companies.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU');
            if (defaultCompany) {
                formik.setFieldValue('company', defaultCompany.value);
            }
            setIsSearched(true);
        }
    }, [getCompaniesList, formik.values.company, isSearched, formik.setFieldValue]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <RequiredInput
                            id='companyName'
                            label="Razón Social"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.companyName}
                        />

                        <Input
                            id='cif'
                            label="CIF"
                            className='md:col-span-12'
                            value={formik.values.cif}
                            onChange={formik.handleChange}
                            error={formik.errors.cif}
                        />

                        <Input
                            id='code'
                            label="Código"
                            className='md:col-span-12'
                            readOnly
                            disabled
                            value={formik.values.code}
                            onChange={formik.handleChange}
                            error={formik.errors.code}
                        />

                        <Input
                            id='commercialName'
                            label="Nombre Comercial"
                            className='md:col-span-12'
                            value={formik.values.commercialName}
                            onChange={formik.handleChange}
                            error={formik.errors.commercialName}
                        />

                        <Input
                            id='activity'
                            label="Actividad"
                            className='md:col-span-12'
                            value={formik.values.activity}
                            onChange={formik.handleChange}
                            error={formik.errors.activity}
                        />

                        <Input
                            id='website'
                            label="Sitio Web"
                            className='md:col-span-12'
                            value={formik.values.website}
                            onChange={formik.handleChange}
                            error={formik.errors.website}
                        />

                        <Input
                            id='email'
                            label="Email"
                            className='md:col-span-12'
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={formik.errors.email}
                        />

                        <PhoneInput
                            id='telephone'
                            label='Teléfono'
                            className="md:col-span-12"
                            onBlur={formik.handleBlur}
                            onChange={e => { formik.setFieldValue('telephone', e) }}
                            value={formik.values.telephone}
                            error={formik.errors.telephone as string}
                            required={false}
                        />

                        <PhoneInput
                            id='telephone2'
                            label='Teléfono 2'
                            className="md:col-span-12"
                            onBlur={formik.handleBlur}
                            onChange={e => { formik.setFieldValue('telephone2', e) }}
                            value={formik.values.telephone2}
                            error={formik.errors.telephone2 as string}
                            required={false}
                        />

                        <CustomSelect
                            required
                            isSearchable
                            id={'retributionType'}
                            label="Tipo de retribución"
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.retributionType, label: retributionTypes.find((g: any) => g.value === formik.values.retributionType)?.label }}
                            options={retributionTypes}
                            onChange={
                                (e: any) => { formik.setFieldValue('retributionType', e?.value) }
                            }
                            error={formik.errors.retributionType}
                        />

                        <Input
                            id='taxDomicile'
                            label="Domicilio fiscal"
                            className='md:col-span-12'
                            value={formik.values.taxDomicile}
                            onChange={formik.handleChange}
                            error={formik.errors.taxDomicile}
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañia"
                                containerClassName='md:col-span-12'
                                value={
                                    formik.values.company ?
                                        { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                        getCompaniesList()?.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                options={getCompaniesList()}
                                onChange={
                                    (e: any) => {
                                        formik.setFieldValue('company', e?.value);
                                        formik.setFieldValue('providerTypeId', '');
                                    }
                                }
                                error={formik.errors.company}
                            />
                        )}

                        <CustomSelect
                            required
                            isSearchable
                            id={'providerTypeId'}
                            label="Tipo de proveedor"
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.providerTypeId, label: getProviderTypesList()?.find((g: any) => g.value === formik.values.providerTypeId)?.label }}
                            options={getProviderTypesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('providerTypeId', e?.value) }
                            }
                            error={formik.errors.providerTypeId}
                        />

                        <Textarea
                            id='comments'
                            label='Comentarios'
                            rows={4}
                            value={formik.values.comments}
                            onChange={(e) => formik.setFieldValue('comments', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />

                        <CustomSelect
                            required
                            isSearchable
                            id={'statusId'}
                            label="Estado"
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.statusId, label: providerStatuses.find((g: any) => g.value === formik.values.statusId?.toString())?.label }}
                            options={providerStatuses}
                            onChange={
                                (e: any) => { formik.setFieldValue('statusId', e?.value) }
                            }
                            error={formik.errors.statusId}
                        />

                        <Switch
                            id='autoInvoice'
                            label='Facturación automática'
                            checked={formik.values.autoInvoice}
                            onChange={(e) => formik.setFieldValue('autoInvoice', e.target.checked)}
                            className="mb-4 md:col-span-12"
                        />
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ProviderForm;
